import React, { useEffect, useMemo, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, Save, Bell, FileText, CreditCard, Receipt, MessageSquare, LayoutTemplate, Calculator, FileSignature, Plus, CheckCircle2, Circle, Trash2, ChevronsUpDown, ChevronDown, ChevronUp, Package, Building2, Bed, RotateCcw, Upload, Eye, Download, Loader2, Image as ImageIcon, Tag, MapPin, ScrollText, ShieldCheck, Coins, Flag, CircleCheck, StickyNote, ClipboardList, ExternalLink, AlertTriangle, Sparkles, Clock, Phone, Hash, Users } from "lucide-react";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Separator } from "@/components/ui/separator";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { api, apiErrorMessage } from "@/lib/api";
import {
  RESERVA_TYPE_LABELS, RESERVA_STATUS_LABELS, RESERVA_STATUS_CLASS,
  RESERVA_OVERRIDE_LABELS, RESERVA_FACTURACION_LABELS, fmtEUR, fmtDate,
} from "@/pages/trips/shared";
import { ReservaTemplate } from "@/pages/trips/ReservaTemplate";
import { HotelNameAutocomplete } from "@/components/HotelNameAutocomplete";
import { FloatingSaveButton } from "@/components/FloatingSaveButton";
import { composeTDDescriptionFromFields, composeTDDescriptionFromFieldsAloj, mergeTDDescription } from "@/pages/trips/ReservaTemplate";
import { FacturaIA } from "@/pages/trips/FacturaIA";
import { SolicitudFacturaReserva } from "@/pages/trips/SolicitudFacturaReserva";
import ReservaChat from "@/pages/trips/ReservaChat";
import { ROOM_TYPES, ROOM_PAX_DEFAULT, BOARD_TYPES, uid } from "@/pages/cotizador/shared";

const TYPE_ORDER = ["alojamiento", "actividad", "entradas", "transfer", "tren", "vuelo", "rental_car", "free_day", "self_drive", "self_guided", "otros"];
const ESTADO_RESERVA_ORDER = ["sin_seleccion", "contacto", "reserva", "confirmacion", "cancelada"];
const FACT_ORDER = ["sin_factura", "factura", "factura_mensual", "facturado_mensual", "no_aplica"];

const F = ({ label, children, className = "" }) => (
  <div className={`space-y-1.5 ${className}`}><Label className="text-xs text-muted-foreground">{label}</Label>{children}</div>
);
const SW = ({ label, checked, onChange, testid, disabled }) => (
  <div className="flex items-center justify-between rounded-md border px-3 py-2">
    <Label className="text-xs font-medium">{label}</Label>
    <Switch checked={!!checked} onCheckedChange={onChange} disabled={disabled} data-testid={testid} />
  </div>
);
// Toggle en línea con altura h-9 fija (mismo alto que Input), para el nuevo layout.
const ToggleH9 = ({ icon: Icon, label, checked, onChange, testid, disabled }) => (
  <div className={`flex h-9 items-center justify-between rounded-md border bg-muted/20 px-3 ${disabled && !checked ? "opacity-70" : ""}`}>
    <Label className="flex items-center gap-1.5 text-xs font-medium">
      {Icon && <Icon className="h-3.5 w-3.5 text-muted-foreground" />}
      {label}
    </Label>
    <Switch
      checked={!!checked}
      onCheckedChange={onChange}
      disabled={disabled}
      className={disabled && checked ? "!opacity-100 data-[state=checked]:!bg-primary" : ""}
      data-testid={testid}
    />
  </div>
);
// Tipos de reserva donde tiene sentido el toggle Private / Small group.
// Exclusivamente `actividad` — el resto (alojamiento, transportes, entradas,
// self_guided, self_drive, free_day, otros, vuelo, tren, rental_car) NO lo muestra.
const SHOW_GROUP_TYPES = new Set(["actividad"]);

// Pill segmentado Private / Small Group — misma altura h-9 que el resto de campos.
// 3 estados: "private" (primary), "small_group" (amber), null (sin marcar).
// Click en el estado activo lo desmarca (volver a null).
const GroupTypePill = ({ value, onChange, testid }) => (
  <div className={`flex h-9 items-center overflow-hidden rounded-md border bg-muted/20`} data-testid={testid}>
    <button
      type="button"
      onClick={() => onChange(value === "private" ? null : "private")}
      className={`flex h-full flex-1 items-center justify-center gap-1 text-[11px] font-semibold uppercase tracking-wider transition ${value === "private" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-muted"}`}
      data-testid={`${testid}-private`}
    >
      Private
    </button>
    <div className="h-full w-px bg-border" />
    <button
      type="button"
      onClick={() => onChange(value === "small_group" ? null : "small_group")}
      className={`flex h-full flex-1 items-center justify-center gap-1 text-[11px] font-semibold uppercase tracking-wider transition ${value === "small_group" ? "bg-amber-500 text-white" : "text-muted-foreground hover:bg-muted"}`}
      data-testid={`${testid}-small-group`}
    >
      Small group
    </button>
  </div>
);

// Campo con label + icono compacto (para el layout de Datos).
const FieldLabel = ({ icon: Icon, label, className = "text-muted-foreground" }) => (
  <Label className={`mb-1 flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider ${className}`}>
    {Icon && <Icon className="h-3 w-3" />}{label}
  </Label>
);
const SelectField = ({ label, value, onChange, options, testid, placeholder = "Elegir…" }) => (
  <div className="space-y-2">
    <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{label}</Label>
    <Select value={value || undefined} onValueChange={onChange}>
      <SelectTrigger data-testid={testid}><SelectValue placeholder={placeholder} /></SelectTrigger>
      <SelectContent>
        {options.map(([k, v]) => <SelectItem key={k} value={k} data-testid={`${testid}-${k}`}>{v}</SelectItem>)}
      </SelectContent>
    </Select>
  </div>
);

const mapType = (t) => {
  const x = (t || "").toLowerCase();
  if (["hotel", "accommodation", "alojamiento", "lodging"].includes(x)) return "alojamiento";
  if (["transfer", "transport", "transporte"].includes(x)) return "transfer";
  return "actividad";
};
const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;

function ProviderTypeahead({ value, onChange }) {
  const [term, setTerm] = useState(value || "");
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  useEffect(() => { setTerm(value || ""); }, [value]);
  useEffect(() => { const t = setTimeout(() => setQ(term), 250); return () => clearTimeout(t); }, [term]);
  const { data, isFetching } = useQuery({
    queryKey: ["provider-typeahead", q],
    queryFn: async () => (await api.get("/providers", { params: { q, solo_viaje: true, limit: 20 } })).data,
    enabled: open && q.trim().length >= 1, keepPreviousData: true,
  });
  const items = data?.items || [];
  const pick = (p) => { const nm = p.nombre_comercial || p.name; onChange({ provider_name: nm, provider_id: p.id }); setTerm(nm); setOpen(false); };
  const type = (v) => { setTerm(v); onChange({ provider_name: v, provider_id: null }); setOpen(true); };
  return (
    <div className="relative">
      <Building2 className="pointer-events-none absolute left-2.5 top-1/2 z-10 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
      <Input value={term} onChange={(e) => type(e.target.value)} onFocus={() => setOpen(true)} onBlur={() => setTimeout(() => setOpen(false), 150)} placeholder="Escribe proveedor…" className="pl-8" autoComplete="off" data-testid="rd-provider-picker" />
      {open && term.trim() && (
        <div className="absolute z-50 mt-1 max-h-64 w-full overflow-auto rounded-md border bg-popover p-1 shadow-md" data-testid="rd-provider-options">
          {isFetching && items.length === 0 && <div className="px-2 py-1.5 text-xs text-muted-foreground">Buscando…</div>}
          {!isFetching && items.length === 0 && <div className="px-2 py-1.5 text-xs text-muted-foreground">Sin coincidencias · se usará el texto escrito.</div>}
          {items.map((p) => (
            <button type="button" key={p.id} onMouseDown={(e) => { e.preventDefault(); pick(p); }} className="flex w-full items-center justify-between gap-2 rounded px-2 py-1.5 text-left text-sm hover:bg-accent" data-testid={`rd-provider-opt-${p.id}`}>
              <span className="truncate font-medium">{p.nombre_comercial || p.name}</span>
              <span className="flex shrink-0 items-center gap-1.5">
                {p.sistema_reserva === "MAIL" && <span className="rounded bg-emerald-100 px-1 text-[9px] font-semibold text-emerald-700">MAIL</span>}
                {p.country ? <span className="text-xs text-muted-foreground">{p.country}</span> : null}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function ProductCombobox({ label, providerName, onPick }) {
  const [open, setOpen] = useState(false);
  const [term, setTerm] = useState("");
  const [q, setQ] = useState("");
  useEffect(() => { const t = setTimeout(() => setQ(term), 300); return () => clearTimeout(t); }, [term]);
  const { data, isFetching } = useQuery({
    queryKey: ["product-search", providerName, q],
    queryFn: async () => (await api.get("/products", { params: { q, provider_name: providerName || undefined, limit: 50 } })).data,
    enabled: open, keepPreviousData: true,
  });
  const items = data?.items || [];
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" role="combobox" className="w-full justify-between font-normal" data-testid="rd-product-picker">
          <span className="flex items-center gap-2 truncate"><Package className="h-4 w-4 shrink-0 text-muted-foreground" />{label || (providerName ? `Producto de ${providerName}…` : "Buscar producto…")}</span>
          <ChevronsUpDown className="h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[420px] p-0" align="start">
        <Command shouldFilter={false}>
          <CommandInput value={term} onValueChange={setTerm} placeholder="Buscar producto…" data-testid="rd-product-search" />
          <CommandList>
            {isFetching && <div className="p-3 text-xs text-muted-foreground">Buscando…</div>}
            <CommandEmpty>Sin productos.</CommandEmpty>
            <CommandGroup>
              {items.map((p) => (
                <CommandItem key={p.id} value={p.id} onSelect={() => { onPick(p); setOpen(false); setTerm(""); }} data-testid={`rd-product-opt-${p.id}`}>
                  <div className="flex w-full items-center justify-between gap-2">
                    <span className="truncate"><span className="font-medium">{p.name}</span> <span className="text-xs text-muted-foreground">· {p.city || p.country || ""}</span></span>
                    <span className="shrink-0 text-xs tabular-nums text-muted-foreground">{fmtEUR(p.cost_tax_incl)}</span>
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

export default function ReservaDetail({ tripId, trip, reserva, recordatorios = [], onBack, onChanged }) {
  const [form, setForm] = useState(() => ({ ...reserva }));
  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));
  const merge = (obj) => setForm((s) => ({ ...s, ...obj }));
  const [saving, setSaving] = useState(false);
  const [googleBusy, setGoogleBusy] = useState(false);
  // React Query client: al guardar la reserva invalidamos la cache del TD
  // para que el Travel Document se refresque automáticamente sin necesidad
  // de que el usuario navegue a la tab y pulse "Actualizar" (2026-07 · Eduardo).
  const qc = useQueryClient();
  // Guarda las queries de Google que ya fallaron para no re-disparar en cada blur.
  const googleFailedRef = useRef(new Set());
  const [cotOpen, setCotOpen] = useState(() => { try { return localStorage.getItem("rd-cot-open") !== "0"; } catch { return true; } });
  const toggleCot = (o) => { setCotOpen(o); try { localStorage.setItem("rd-cot-open", o ? "1" : "0"); } catch {} };
  const tripAdults = trip.adults != null ? trip.adults : Math.max(0, (trip.number_of_travelers || 1) - (trip.number_of_children || 0));
  const addPago = () => set("pagos", [...(form.pagos || []), { fecha: "", medio: "", cantidad: "", tipo: "pago", notas: "" }]);
  const addDevolucion = () => set("pagos", [...(form.pagos || []), { fecha: "", medio: "", cantidad: "", tipo: "devolucion", notas: "" }]);
  const setPago = (i, k, v) => set("pagos", (form.pagos || []).map((p, idx) => (idx === i ? { ...p, [k]: v } : p)));
  const delPago = (i) => set("pagos", (form.pagos || []).filter((_, idx) => idx !== i));
  const pagosSum = (form.pagos || []).filter((p) => p.tipo !== "devolucion").reduce((a, p) => a + (parseFloat(p.cantidad) || 0), 0);
  const devolucionesSum = (form.pagos || []).filter((p) => p.tipo === "devolucion").reduce((a, p) => a + (parseFloat(p.cantidad) || 0), 0);
  const pagadoNeto = pagosSum - devolucionesSum;
  const { data: metodosPago = [] } = useQuery({
    queryKey: ["metodos-pago-activos"],
    queryFn: async () => (await api.get("/metodos-pago", { params: { only_active: true } })).data,
  });
  const TIPO_GRP = { tarjeta: "Tarjetas", transferencia: "Transferencias", domiciliacion: "Domiciliaciones" };
  const mediosByTipo = useMemo(() => { const g = {}; metodosPago.forEach((m) => { (g[m.tipo] = g[m.tipo] || []).push(m); }); return g; }, [metodosPago]);
  const medioLabels = useMemo(() => new Set(metodosPago.map((m) => m.label)), [metodosPago]);

  const num = (v) => (v === "" || v === null || v === undefined ? 0 : parseFloat(v) || 0);
  const int = (v) => (v === "" || v === null || v === undefined ? 0 : parseInt(v) || 0);

  // ─── Google Places · autocompletar Ficha del hotel ───────────────────
  // Consulta Google Places API (New) por nombre + ciudad + país y rellena
  // hotel_address, hotel_website y td_contact_phone si vienen. El backend
  // cachea 30 días para no repetir gasto en el mismo hotel.
  const runGoogleLookup = async ({ silent = false, force = false } = {}) => {
    const name = (form.hotel_name || form.service || "").trim();
    if (name.length < 3) {
      if (!silent) toast.info("Escribe al menos 3 caracteres del nombre del hotel.");
      return;
    }
    const key = `${name.toLowerCase()}|${(form.city || "").toLowerCase()}|${(form.country || "").toLowerCase()}`;
    if (!force && googleFailedRef.current.has(key)) return;   // ya falló, no reintentes en blur
    setGoogleBusy(true);
    try {
      const { data } = await api.post("/hotels/google-lookup", {
        name,
        city: form.city || "",
        country: form.country || "",
        force_refresh: !!force,
      });
      if (!data?.found) {
        googleFailedRef.current.add(key);
        if (data?.error === "api_key_missing") {
          if (!silent) toast.error("Falta configurar GOOGLE_MAPS_API_KEY en el backend.");
        } else if (!silent) toast.warning(`Google no encontró "${name}".`);
        return;
      }
      const patch = {};
      if (data.address && !form.hotel_address) patch.hotel_address = data.address;
      if (data.website && !form.hotel_website) patch.hotel_website = data.website;
      // El teléfono va al campo del hotel (`hotel_phone`) cuando es alojamiento.
      // Para otros tipos (poco común pegar aquí) mantenemos el fallback en
      // `td_contact_phone` que es el campo de guía/conductor.
      if (data.phone) {
        if (form.type === "alojamiento" && !form.hotel_phone) patch.hotel_phone = data.phone;
        else if (!form.td_contact_phone) patch.td_contact_phone = data.phone;
      }
      // Si el nombre de Google es más completo que el actual y el actual venía vacío o del service crudo, lo usamos.
      if (data.name && (!form.hotel_name || form.hotel_name === form.service)) patch.hotel_name = data.name;
      if (Object.keys(patch).length) {
        merge(patch);
        toast.success(data.cached ? "Datos del hotel cargados (cache)." : "Datos del hotel autocompletados desde Google.");
      } else if (!silent) {
        toast.info("Los campos ya estaban rellenos · nada que autocompletar.");
      }
    } catch (e) {
      if (!silent) toast.error(apiErrorMessage(e, "Error consultando Google Places"));
    } finally {
      setGoogleBusy(false);
    }
  };

  const nights = useMemo(() => {
    if (!form.date_entry || !form.date_exit) return 0;
    const d = Math.round((new Date(form.date_exit) - new Date(form.date_entry)) / 86400000);
    return d > 0 ? d : 0;
  }, [form.date_entry, form.date_exit]);

  // Transfers y trenes no llevan Meeting Point (Ventas, oct-2026).
  const noMeetingPoint = form.type === "transfer" || form.type === "tren";

  // Teléfono de contacto: al elegir proveedor se arrastra su teléfono al campo «Contacto»
  // (si está vacío). El operador lo sustituye a mano si le dan el del guía/conductor.
  const provQ = useQuery({
    queryKey: ["provider-td", form.provider_id],
    queryFn: async () => (await api.get(`/providers/${form.provider_id}`)).data,
    enabled: !!form.provider_id, staleTime: 5 * 60_000,
  });
  const providerPhone = provQ.data?.telefono_reservas || provQ.data?.telefono || provQ.data?.phone || "";
  const phoneAppliedRef = useRef(null);
  const autoPhoneRef = useRef("");   // último teléfono puesto automáticamente (para no pisar los manuales)
  useEffect(() => {
    if (!form.provider_id || !providerPhone || phoneAppliedRef.current === form.provider_id) return;
    phoneAppliedRef.current = form.provider_id;
    const current = (form.td_contact_phone || "").trim();
    // Rellena si está vacío o si lo que hay es el teléfono automático del proveedor anterior.
    if (!current || current === autoPhoneRef.current) {
      autoPhoneRef.current = providerPhone;
      merge({ td_contact_phone: providerPhone });
    }
  }, [form.provider_id, providerPhone]); // eslint-disable-line react-hooks/exhaustive-deps

  // Documentos reales de la reserva (vouchers / facturas): alimentan los estados y las
  // validaciones al guardar. Mismas queryKeys que las pestañas → se refrescan al subir.
  const vouchersQ = useQuery({
    queryKey: ["vouchers", tripId, reserva.id],
    queryFn: async () => (await api.get(`/trips/${tripId}/reservas/${reserva.id}/vouchers`)).data,
  });
  const facturasQ = useQuery({
    queryKey: ["factura-reserva", tripId, reserva.id],
    queryFn: async () => (await api.get(`/trips/${tripId}/reservas/${reserva.id}/factura`)).data,
  });
  const voucherCount = (vouchersQ.data?.vouchers || form.voucher_docs || []).length;
  const facturaCount = (facturasQ.data?.facturas || form.factura_docs || []).length;
  const pagosValidos = (form.pagos || []).filter((p) => p.tipo !== "devolucion" && num(p.cantidad) > 0 && (p.fecha || p.medio)).length;

  const rooms = form.room_items || [];
  const roomsMode = form.type === "alojamiento" && rooms.length > 0;
  const nightsEff = Math.max(1, nights);
  const sumNightExcl = rooms.reduce((s, r) => s + (num(r.cost_excl) || num(r.cost_incl)), 0);
  const sumNightIncl = rooms.reduce((s, r) => s + (num(r.cost_incl) || num(r.cost_excl)), 0);
  const accTotalExcl = round2(sumNightExcl * nightsEff);
  const accTotalIncl = round2(sumNightIncl * nightsEff);
  const roomsPax = rooms.reduce((s, r) => s + (int(r.pax) || 0), 0);
  const addRoom = () => set("room_items", [...rooms, { id: uid(), room_type: "doble", pax: 2, cost_incl: 0, cost_excl: 0 }]);
  const delRoom = (id) => set("room_items", rooms.filter((r) => r.id !== id));
  const patchRoom = (id, patch) => set("room_items", rooms.map((r) => {
    if (r.id !== id) return r;
    const m = { ...r, ...patch };
    if ("room_type" in patch && !("pax" in patch)) m.pax = ROOM_PAX_DEFAULT[patch.room_type] || m.pax;
    if ("cost_incl" in patch) m.cost_excl = round2(num(patch.cost_incl));
    return m;
  }));
  const roomSummary = useMemo(() => {
    const c = {};
    (form.room_items || []).forEach((r) => { const k = r.room_type || "otro"; c[k] = (c[k] || 0) + 1; });
    return Object.entries(c).map(([k, n]) => `${n} ${k.charAt(0).toUpperCase()}${k.slice(1)}`).join(" · ");
  }, [form.room_items]);

  // ─── Grupos de habitaciones (UI de alojamiento) ──────────────────────
  // Estructura: `[{count, type_text}, ...]`. Se serializa a los campos
  // existentes en BD sin schema change:
  //   · `rooms`  = suma de todos los counts (Nº habitaciones total)
  //   · `room`   = texto plano "2 Suites · 1 Twin · 1 Standard"
  const parseRoomGroups = (roomStr, roomsN) => {
    if (!roomStr) return [{ id: uid(), count: parseInt(roomsN, 10) || 1, type_text: "" }];
    // "2 Suites · 1 Twin · 1 Standard"  o  "1 Doble Deluxe + 1 Twin"
    const parts = String(roomStr).split(/\s*[·+,]\s*/).filter(Boolean);
    const parsed = parts.map((chunk) => {
      const m = chunk.trim().match(/^(\d+)\s+(.+)$/);
      return m
        ? { id: uid(), count: parseInt(m[1], 10) || 1, type_text: m[2].trim() }
        : { id: uid(), count: 1, type_text: chunk.trim() };
    });
    return parsed.length ? parsed : [{ id: uid(), count: parseInt(roomsN, 10) || 1, type_text: "" }];
  };
  // Estado local, inicializado desde form.room (una vez)
  const [roomGroups, setRoomGroups] = useState(() => parseRoomGroups(form.room, form.rooms));
  // Cuando cambia el trip (reset), re-parsear desde form.room
  React.useEffect(() => {
    setRoomGroups(parseRoomGroups(form.room, form.rooms));
  }, [form.id]);

  const serializeGroups = (groups) => {
    const totalRooms = groups.reduce((s, g) => s + (parseInt(g.count, 10) || 0), 0);
    const text = groups
      .filter((g) => g.type_text && (parseInt(g.count, 10) || 0) > 0)
      .map((g) => `${parseInt(g.count, 10) || 1} ${g.type_text}`)
      .join(" · ");
    return { totalRooms, text };
  };
  const applyGroups = (next) => {
    setRoomGroups(next);
    const { totalRooms, text } = serializeGroups(next);
    setForm((s) => ({ ...s, rooms: totalRooms || 1, room: text }));
  };
  const patchGroup = (id, patch) => applyGroups(roomGroups.map((g) => (g.id === id ? { ...g, ...patch } : g)));
  const addGroup = () => applyGroups([...roomGroups, { id: uid(), count: 1, type_text: "" }]);
  const delGroup = (id) => applyGroups(roomGroups.length > 1 ? roomGroups.filter((g) => g.id !== id) : roomGroups);
  const totalRoomsFromGroups = roomGroups.reduce((s, g) => s + (parseInt(g.count, 10) || 0), 0);

  const productLabel = form.product_id
    ? (roomsMode ? `${form.service} · ${rooms.every((r) => r.room_type === rooms[0].room_type) ? rooms[0].room_type : "mixto"}` : form.service)
    : "";
  const costExclCalc = roomsMode ? accTotalExcl : num(form.cost_tax_excl);
  const costInclCalc = roomsMode ? accTotalIncl : num(form.cost_tax_incl);
  // Cotizado Real = TOTAL del coste del servicio: nunca se multiplica (Dirección, oct-2026).
  // `product_quantity` solo sirve para calcular el total al elegir un producto del catálogo.
  const unitsEff = int(form.product_quantity) || 1;
  const qtyCalc = 1;

  const { data: calc } = useQuery({
    queryKey: ["reserva-calc", trip.partner, trip.fx_rate, form.country, costExclCalc, costInclCalc, form.override, form.price, qtyCalc],
    queryFn: async () => (await api.post("/formulas/calc", {
      partner: trip.partner || "other", fx_rate: trip.fx_rate || null,
      items: [{ description: form.service || "", country: form.country || "", cost_tax_excl: costExclCalc, cost_tax_incl: costInclCalc, qty: qtyCalc, override: form.override || "no", price: num(form.price) }],
    })).data,
  });
  const cotizadoReal = calc?.result?.totals?.subtotal_quoted;
  const units = qtyCalc;
  const totalExcl = roomsMode ? accTotalExcl : num(form.cost_tax_excl) * units;
  const totalIncl = roomsMode ? accTotalIncl : num(form.cost_tax_incl) * units;
  const beneficio = cotizadoReal != null ? cotizadoReal - totalExcl : null;
  const margenPct = cotizadoReal ? (beneficio / cotizadoReal) * 100 : 0;

  const { data: citiesData } = useQuery({
    queryKey: ["product-cities"],
    queryFn: async () => (await api.get("/products/cities")).data,
  });
  const cityCountry = useMemo(() => {
    const m = {};
    (citiesData?.items || []).forEach((c) => { if (c.city) m[c.city.toLowerCase()] = c.country; });
    return m;
  }, [citiesData]);
  const cityList = useMemo(() => (citiesData?.items || []).map((c) => c.city).filter(Boolean), [citiesData]);

  const onProduct = (p) => {
    const t = mapType(p.type);
    const base = {
      product_id: p.id, provider_name: form.provider_name || p.provider_name, provider_id: form.provider_id || p.provider_id,
      service: form.service || p.name, city: p.city || form.city, country: p.country || form.country,
      type: t, currency: p.currency || "EUR",
    };
    if (t === "alojamiento") {
      const incl = num(p.cost_tax_incl) || num(p.cost_tax_excl);
      const excl = num(p.cost_tax_excl) || incl;
      const cur = form.room_items || [];
      const room_items = cur.length
        ? cur.map((r) => (num(r.cost_incl) ? r : { ...r, cost_incl: incl, cost_excl: excl }))
        : [{ id: uid(), room_type: "doble", pax: 2, cost_incl: incl, cost_excl: excl }];
      merge({ ...base, room_items });
    } else {
      merge({ ...base, cost_tax_excl: p.cost_tax_excl, cost_tax_incl: p.cost_tax_incl });
    }
  };
  const onCity = (v) => {
    const c = cityCountry[(v || "").trim().toLowerCase()];
    merge(c ? { city: v, country: c } : { city: v });
  };

  const save = async () => {
    // Alertas de coherencia (Ventas, oct-2026): no se guarda si el estado no tiene soporte documental.
    const isPlaceholder = !!form.booked_by_kimkim || ["free_day", "self_drive", "self_guided"].includes(form.type);
    const problems = [];
    if (form.status_facturacion === "factura" && facturaCount === 0) problems.push("Has marcado «Factura» pero no hay ningún archivo de factura subido (pestaña Facturas).");
    if (form.status_pago && pagosValidos === 0) problems.push("Has marcado el servicio como pagado pero no hay ningún pago registrado con fecha/método e importe (pestaña Pagos).");
    if (!isPlaceholder && ["reserva", "confirmacion"].includes(form.status_reserva) && form.status_reserva !== reserva.status_reserva && voucherCount === 0) problems.push("Has marcado el servicio como reservado pero no hay voucher/proforma subido (pestaña Vouchers).");
    if (form.status_proforma_voucher && voucherCount === 0) problems.push("Has marcado «Proforma/Voucher» pero no hay ningún voucher subido (pestaña Vouchers).");
    if (problems.length) {
      problems.forEach((p) => toast.error(p, { duration: 7000 }));
      return;
    }
    setSaving(true);
    try {
      // Auto-regeneración del template en cada save (2026-07, feedback Eduardo):
      // Si el operador modificó Meeting Point, Hora, Check-in, Board, etc.,
      // el sales_description debe reflejarlo sin obligarle a pulsar "Guardar
      // como formato nuevo". Regeneramos silenciosamente cuando el tipo tiene
      // template estructurado y hay al menos UN td_* field con datos (así no
      // pisamos descripciones libres de reservas antiguas sin TD fields).
      // El teléfono autocompletado desde el proveedor NO cuenta: así no se regenera (ni se pierde)
      // una descripción libre antigua solo por abrir la reserva.
      const manualPhone = form.td_contact_phone && form.td_contact_phone !== autoPhoneRef.current;
      const hasTDData = Boolean(
        form.td_meeting_point || form.td_guide_name || manualPhone
        || form.td_description || form.td_included || form.td_advises
        || form.td_starting_time || form.td_duration || form.td_drop_off
        || form.td_hotel_name || form.td_check_in || form.td_check_out
        || form.td_reservation_name || form.confirmation_number,
      );
      let regeneratedDesc = form.sales_description || "";
      if (hasTDData) {
        // Los campos se regeneran SIN pisar el texto de la plantilla / texto libre (nov-2026).
        if (form.type === "alojamiento") {
          regeneratedDesc = mergeTDDescription(composeTDDescriptionFromFieldsAloj(form), form.sales_description);
        } else if (["actividad", "transfer", "tren", "vuelo", "rental_car", "excursion"].includes(form.type)) {
          regeneratedDesc = mergeTDDescription(composeTDDescriptionFromFields(form), form.sales_description);
        }
      }
      await api.put(`/trips/${tripId}/reservas/${reserva.id}`, {
        service: form.service, type: form.type, city: form.city, country: form.country,
        quantity: int(form.quantity) || 1, number_of_children: int(form.number_of_children),
        date_entry: form.date_entry || null, date_exit: form.date_exit || null,
        hour_entry: form.hour_entry, hour_end: form.hour_end || "",
        start_location: form.start_location || "", end_location: form.end_location || "",
        confirmation_number: form.confirmation_number || "",
        room: roomsMode ? roomSummary : form.room, rooms: roomsMode ? rooms.length : int(form.rooms) || 1,
        board: form.board || (form.type === "alojamiento" ? "bb" : null),
        room_items: rooms.map((r) => ({ id: r.id, room_type: r.room_type || "doble", pax: int(r.pax) || 1, cost_incl: num(r.cost_incl), cost_excl: num(r.cost_excl) })),
        contactado: !!form.contactado, provider_id: form.provider_id || null, provider_name: form.provider_name,
        status_reserva: form.status_reserva || "sin_seleccion",
        override: form.override || "no", price: num(form.price),
        cost_tax_excl: roomsMode ? accTotalExcl : num(form.cost_tax_excl), cost_tax_incl: roomsMode ? accTotalIncl : num(form.cost_tax_incl),
        kk_price_total: num(form.kk_price_total),
        product_id: form.product_id || null, product_quantity: roomsMode ? 1 : unitsEff,
        producto_2_id: form.producto_2_id || null, product_quantity_product2: int(form.product_quantity_product2),
        invoice_tax_excl: num(form.invoice_tax_excl), invoice_tax_incl: num(form.invoice_tax_incl),
        reembolsado: num(form.reembolsado), proveedor_mensual: !!form.proveedor_mensual,
        pagos: (form.pagos || []).filter((p) => p.fecha || p.medio || num(p.cantidad) || p.notas).map((p) => ({ fecha: p.fecha || null, medio: p.medio || null, cantidad: num(p.cantidad), tipo: p.tipo === "devolucion" ? "devolucion" : "pago", notas: p.notas || null })),
        status_conciliado: !!form.status_conciliado,
        status_facturacion: form.status_facturacion || "sin_factura", factura_solicitada: !!form.factura_solicitada,
        status_proforma_voucher: !!form.status_proforma_voucher || voucherCount > 0, status_pago: !!form.status_pago, flag: !!form.flag, note: form.note,
        group_type: form.group_type || null,
        plantilla_id: form.plantilla_id || "", plantilla_name: form.plantilla_name || "",
        sales_description: regeneratedDesc, sales_images: form.sales_images || [],
        hotel_name: form.hotel_name || "", hotel_address: form.hotel_address || "",
        hotel_website: form.hotel_website || "", hotel_phone: form.hotel_phone || "",
        hide_website: !!form.hide_website,
        td_starting_time: form.td_starting_time || "",
        td_duration: form.td_duration || "",
        td_meeting_point: noMeetingPoint ? "" : (form.td_meeting_point || ""),
        td_meeting_point_url: noMeetingPoint ? "" : (form.td_meeting_point_url || ""),
        td_drop_off: form.td_drop_off || "",
        td_drop_off_url: form.td_drop_off_url || "",
        td_contact_phone: form.td_contact_phone || "",
        td_guide_name: form.td_guide_name || "",
        td_advises: form.td_advises || "",
        td_description: form.td_description || "",
        td_included: form.td_included || "",
        td_hotel_map_url: form.td_hotel_map_url || "",
        td_reservation_name: form.td_reservation_name || "",
        exclude_from_td: !!form.exclude_from_td,
      });
      // Refresca sales_description en el form local con el regenerado, así
      // el editor RichText muestra el nuevo HTML sin necesidad de recargar.
      if (hasTDData && regeneratedDesc !== form.sales_description) {
        merge({ sales_description: regeneratedDesc });
      }
      toast.success("Reserva guardada · Template actualizado");
      // Invalidamos la cache del Travel Document para que se refresque
      // automáticamente sin que el usuario tenga que ir a la tab TD.
      qc.invalidateQueries({ queryKey: ["td", tripId] });
      onChanged && onChanged();
    } catch (e) { toast.error(apiErrorMessage(e)); }
    setSaving(false);
  };

  const recs = useMemo(() => recordatorios.filter((r) => r.reserva_id === reserva.id), [recordatorios, reserva.id]);

  return (
    <div className="space-y-6 pb-24" data-testid="reserva-detail">
      <FloatingSaveButton
        onClick={save}
        saving={saving}
        testid="save-reserva-detail-fab"
      />

      {/* Cabecera */}
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-3">
          <Button variant="outline" size="icon" className="h-9 w-9" onClick={onBack} data-testid="reserva-back"><ArrowLeft className="h-4 w-4" /></Button>
          <div>
            <p className="font-heading text-lg font-bold">{form.service || "Reserva"}</p>
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Badge variant="outline" className="text-[10px]">{RESERVA_TYPE_LABELS[form.type] || form.type}</Badge>
              <span className={`rounded px-1.5 py-0.5 text-[11px] ${RESERVA_STATUS_CLASS[form.status_reserva]}`}>{RESERVA_STATUS_LABELS[form.status_reserva]}</span>
            </div>
          </div>
        </div>
      </div>

      {/* ═════════════════ MÓDULO 1 · DATOS ═════════════════
          Rediseño feb-2026 (esquema Eduardo): sección siempre visible con
          la información completa de la reserva. Fila 3 se adapta según el
          tipo de servicio (alojamiento → check-in/out/noches/régimen/flag).
          Nota: el panel LEAD/AD/CH/Agentes vive ahora en TripDetail (superior
          fijo bajo el título), evitando la redundancia en cada reserva. */}
      <section className="rounded-lg border border-primary/25 bg-card shadow-sm" data-testid="rd-modulo-datos">
        <header className="flex items-center justify-between gap-3 border-b bg-gradient-to-r from-primary/5 to-transparent px-5 py-3">
          <div className="flex items-center gap-2">
            <ClipboardList className="h-4 w-4 text-primary" />
            <h2 className="font-heading text-base font-bold">Datos</h2>
          </div>
          {/* Estado Reserva + Flag en el header del módulo (compactos).
              Antes vivían en filas separadas dentro del cuerpo; ahora se elevan
              aquí para dar más espacio a los campos principales (2026-02). */}
          <div className="flex items-center gap-3">
            <Select value={form.status_reserva || "sin_seleccion"} onValueChange={(v) => set("status_reserva", v)}>
              <SelectTrigger className="h-8 border-primary/40 bg-primary/5 text-sm font-semibold" data-testid="rd-estado-reserva">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {ESTADO_RESERVA_ORDER.map((k) => <SelectItem key={k} value={k} data-testid={`rd-estado-reserva-${k}`}>{RESERVA_STATUS_LABELS[k]}</SelectItem>)}
              </SelectContent>
            </Select>
            <ToggleH9 icon={Flag} label="Flag" checked={form.flag} onChange={(v) => set("flag", v)} testid="rd-flag" />
          </div>
        </header>

        <div className="space-y-5 p-6">
          {form.type === "alojamiento" ? (
            /* ─── LAYOUT ALOJAMIENTO (2026-02, diseño Eduardo) ─────────────
               Fila 1: Check-in · Check-out · Noches · Ciudad · Tipo Servicio
               Fila 2: Servicio · Nº hab · Tipo(s) hab (texto) · Régimen
               La ficha detallada (habitaciones estructuradas) vive en el tab
               «Template» — no aquí. Estado + Flag en el header del módulo. */
            <>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-12" data-testid="rd-fila1-aloj">
                <div className="sm:col-span-2">
                  <FieldLabel icon={Circle} label="Check-in" />
                  <Input type="date" className="h-9" value={(form.date_entry || "").slice(0, 10)} onChange={(e) => set("date_entry", e.target.value)} data-testid="rd-start" />
                </div>
                <div className="sm:col-span-2">
                  <FieldLabel icon={Circle} label="Check-out" />
                  <Input type="date" className="h-9" value={(form.date_exit || "").slice(0, 10)} onChange={(e) => set("date_exit", e.target.value)} data-testid="rd-end" />
                </div>
                <div className="sm:col-span-1">
                  <FieldLabel label="Noches" />
                  <Input readOnly disabled className="h-9 bg-muted/40 tabular-nums text-center" value={nights} data-testid="rd-nights" />
                </div>
                <div className="sm:col-span-4">
                  <FieldLabel icon={MapPin} label="Ciudad" />
                  <Input list="rd-cities-list" className="h-9" value={form.city || ""} onChange={(e) => onCity(e.target.value)} placeholder="Empieza a escribir…" data-testid="rd-city" />
                  <datalist id="rd-cities-list">{cityList.map((c) => <option key={c} value={c} />)}</datalist>
                  {form.country ? <p className="mt-0.5 text-[10px] text-muted-foreground">País: <span className="font-medium">{form.country}</span></p> : null}
                  <input type="hidden" value={form.country || ""} onChange={(e) => set("country", e.target.value)} data-testid="rd-country" />
                </div>
                <div className="sm:col-span-3">
                  <FieldLabel icon={Tag} label="Tipo servicio" />
                  <Select value={form.type || undefined} onValueChange={(v) => set("type", v)}>
                    <SelectTrigger className="h-9" data-testid="rd-type"><SelectValue placeholder="Elegir…" /></SelectTrigger>
                    <SelectContent>{TYPE_ORDER.map((k) => <SelectItem key={k} value={k} data-testid={`rd-type-${k}`}>{RESERVA_TYPE_LABELS[k]}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-12" data-testid="rd-fila2-aloj">
                <div className="sm:col-span-3">
                  <FieldLabel icon={ScrollText} label="Servicio" />
                  <Input className="h-9" value={form.service || ""} onChange={(e) => set("service", e.target.value)} placeholder="Nombre del hotel" data-testid="rd-service" />
                </div>
                <div className="sm:col-span-1">
                  <FieldLabel icon={Users} label={`Pax`} />
                  <Input type="number" min={1} className="h-9 tabular-nums text-center" value={form.quantity ?? ""} onChange={(e) => set("quantity", int(e.target.value) || 1)} placeholder={String(tripAdults)} title={`Pax alojados (viaje: ${tripAdults})`} data-testid="rd-pax" />
                </div>
                <div className="sm:col-span-2">
                  <FieldLabel icon={Bed} label="Nº habitaciones" />
                  <Input
                    type="number"
                    min="1"
                    className="h-9 tabular-nums text-center"
                    value={totalRoomsFromGroups || 1}
                    onChange={(e) => {
                      // Editable directamente: colapsa/expande la fila 1 de
                      // "Tipos de habitación" a esa cantidad, preservando el
                      // tipo de la primera fila. Elimina filas extra.
                      const n = Math.max(1, parseInt(e.target.value, 10) || 1);
                      const firstType = roomGroups[0]?.type_text || "";
                      applyGroups([{ id: roomGroups[0]?.id || uid(), count: n, type_text: firstType }]);
                    }}
                    title="Nº total de habitaciones. Se sincroniza con el detalle 'Tipos de habitación' de abajo."
                    data-testid="rd-rooms"
                  />
                </div>
                <div className="sm:col-span-3">
                  <FieldLabel label="Board" />
                  <Select value={form.board || "bb"} onValueChange={(v) => set("board", v)}>
                    <SelectTrigger className="h-9" data-testid="rd-board"><SelectValue /></SelectTrigger>
                    <SelectContent>{BOARD_TYPES.map((b) => <SelectItem key={b.value} value={b.value} data-testid={`rd-board-${b.value}`}>{b.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="sm:col-span-3">
                  <FieldLabel icon={Building2} label="Proveedor" />
                  <ProviderTypeahead value={form.provider_name} onChange={merge} />
                </div>
              </div>
              {/* Grupos de habitaciones: [cantidad] [tipo texto] · repetible.
                  Se serializa a `form.rooms` (total) + `form.room` (texto plano). */}
              <div className="rounded-lg border bg-muted/20 p-3" data-testid="rd-room-groups">
                <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                  <div className="flex items-center gap-1.5">
                    <Bed className="h-3.5 w-3.5 text-primary" />
                    <Label className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Tipos de habitación</Label>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="flex items-center gap-1.5">
                      <Label className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground whitespace-nowrap">Booking ID</Label>
                      <Input
                        className="h-7 w-40 text-xs"
                        value={form.confirmation_number || ""}
                        onChange={(e) => set("confirmation_number", e.target.value)}
                        placeholder="Ej. BKG-12345"
                        data-testid="rd-booking-id-aloj"
                      />
                    </div>
                    <Button type="button" size="sm" variant="outline" className="h-7 text-xs" onClick={addGroup} data-testid="rd-add-room-group">
                      <Plus className="mr-1 h-3 w-3" />Añadir tipo
                    </Button>
                  </div>
                </div>
                <div className="space-y-2">
                  {roomGroups.map((g, idx) => (
                    <div key={g.id} className="flex items-center gap-2" data-testid={`rd-room-group-${idx}`}>
                      <Input
                        type="number"
                        min="1"
                        className="h-9 w-16 tabular-nums text-center"
                        value={g.count}
                        onChange={(e) => patchGroup(g.id, { count: parseInt(e.target.value, 10) || 1 })}
                        title="Cantidad"
                        data-testid={`rd-room-group-count-${idx}`}
                      />
                      <span className="text-xs text-muted-foreground">×</span>
                      <Input
                        className="h-9 flex-1"
                        value={g.type_text}
                        onChange={(e) => patchGroup(g.id, { type_text: e.target.value })}
                        placeholder="Suites / Twin / Doble Deluxe / Standard…"
                        data-testid={`rd-room-group-type-${idx}`}
                      />
                      {roomGroups.length > 1 && (
                        <Button type="button" variant="ghost" size="icon" className="h-8 w-8" onClick={() => delGroup(g.id)} data-testid={`rd-room-group-del-${idx}`}>
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </>
          ) : (
            /* ─── LAYOUT NO-ALOJAMIENTO (2026-02, diseño Eduardo) ───────────
               Fila 1: Día · Ciudad · Hora · Tipo Servicio · Grupo
               Fila 2: Servicio · Proveedor
               Estado + Flag ya viven en el header del módulo. */
            <>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-12">
                <div className="sm:col-span-2">
                  <FieldLabel icon={Circle} label="Día" />
                  <Input type="date" className="h-9" value={(form.date_entry || "").slice(0, 10)} onChange={(e) => set("date_entry", e.target.value)} data-testid="rd-start" />
                </div>
                <div className="sm:col-span-2">
                  <FieldLabel icon={MapPin} label="Ciudad" />
                  <Input list="rd-cities-list" className="h-9" value={form.city || ""} onChange={(e) => onCity(e.target.value)} placeholder="Empieza a escribir…" data-testid="rd-city" />
                  <datalist id="rd-cities-list">{cityList.map((c) => <option key={c} value={c} />)}</datalist>
                  {form.country ? <p className="mt-0.5 text-[10px] text-muted-foreground">País: <span className="font-medium">{form.country}</span></p> : null}
                  <input type="hidden" value={form.country || ""} onChange={(e) => set("country", e.target.value)} data-testid="rd-country" />
                </div>
                <div className="sm:col-span-2">
                  <FieldLabel icon={Circle} label={form.type === "rental_car" ? "Hora recogida" : "Hora"} />
                  <Input className="h-9" value={form.hour_entry || ""} onChange={(e) => set("hour_entry", e.target.value)} placeholder="18:00" data-testid="rd-hour" />
                </div>
                <div className="sm:col-span-2">
                  <FieldLabel icon={Tag} label="Tipo servicio" />
                  <Select value={form.type || undefined} onValueChange={(v) => set("type", v)}>
                    <SelectTrigger className="h-9" data-testid="rd-type"><SelectValue placeholder="Elegir…" /></SelectTrigger>
                    <SelectContent>{TYPE_ORDER.map((k) => <SelectItem key={k} value={k} data-testid={`rd-type-${k}`}>{RESERVA_TYPE_LABELS[k]}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="sm:col-span-2">
                  <FieldLabel icon={Users} label={`Pax (viaje: ${tripAdults + int(trip.number_of_children || 0)})`} />
                  <Input type="number" min={1} className="h-9" value={form.quantity ?? ""} onChange={(e) => set("quantity", int(e.target.value) || 1)} placeholder={String(tripAdults)} data-testid="rd-pax" />
                </div>
                <div className="sm:col-span-2">
                  {SHOW_GROUP_TYPES.has(form.type) ? (
                    <>
                      <FieldLabel label="Grupo" />
                      <GroupTypePill value={form.group_type} onChange={(v) => set("group_type", v)} testid="rd-group-type" />
                    </>
                  ) : null}
                </div>
              </div>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-12">
                <div className="sm:col-span-6">
                  <FieldLabel icon={ScrollText} label="Servicio" />
                  <Input className="h-9" value={form.service || ""} onChange={(e) => set("service", e.target.value)} placeholder="Ej. Traslado, actividad, entrada…" data-testid="rd-service" />
                </div>
                <div className="sm:col-span-6">
                  <FieldLabel icon={Building2} label="Proveedor" />
                  <ProviderTypeahead value={form.provider_name} onChange={merge} />
                </div>
              </div>
              {/* Datos operativos comunes (Booking ID / Duración / Meeting Point / Contacto).
                  Se sincronizan con el Travel Document automáticamente (build_td_snapshot
                  lee de estos campos). Para rental_car el Booking ID vive en su bloque
                  especializado (confirmation_number), por eso lo ocultamos aquí. */}
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-12" data-testid="rd-td-operativos">
                {form.type !== "rental_car" && (
                  <div className="sm:col-span-3">
                    <FieldLabel icon={Hash} label="Booking ID" />
                    <Input className="h-9" value={form.confirmation_number || ""} onChange={(e) => set("confirmation_number", e.target.value)} placeholder="Ej. BKG-12345" data-testid="rd-booking-id" />
                  </div>
                )}
                <div className={form.type === "rental_car" ? "sm:col-span-4" : "sm:col-span-2"}>
                  <FieldLabel icon={Clock} label="Duración" />
                  <Input className="h-9" value={form.td_duration || ""} onChange={(e) => set("td_duration", e.target.value)} placeholder="Ej. 3h · half-day" data-testid="rd-td-duration" />
                </div>
                {!noMeetingPoint && (
                  <div className={form.type === "rental_car" ? "sm:col-span-4" : "sm:col-span-4"}>
                    <FieldLabel icon={MapPin} label="Meeting point" />
                    <Input className="h-9" value={form.td_meeting_point || ""} onChange={(e) => set("td_meeting_point", e.target.value)} placeholder="Dirección legible" data-testid="rd-td-meeting-point" />
                  </div>
                )}
                <div className={form.type === "rental_car" ? "sm:col-span-4" : (noMeetingPoint ? "sm:col-span-7" : "sm:col-span-3")}>
                  <FieldLabel icon={Phone} label="Contacto" />
                  <Input className={`h-9 ${!(form.td_contact_phone || "").trim() ? "border-amber-400 bg-amber-50/40" : ""}`} value={form.td_contact_phone || ""} onChange={(e) => set("td_contact_phone", e.target.value)} placeholder={providerPhone ? `Tel. del proveedor: ${providerPhone}` : "Tel. guía / conductor"} data-testid="rd-td-contact-phone" />
                  <p className="mt-0.5 text-[10px] text-muted-foreground" data-testid="rd-td-contact-hint">
                    {!(form.td_contact_phone || "").trim()
                      ? (form.provider_id ? (providerPhone ? "Se rellenará con el teléfono del proveedor." : "El proveedor no tiene teléfono en su ficha: añádelo a mano para que el TD no salga sin contacto.") : "Elige un proveedor para arrastrar su teléfono, o escríbelo a mano.")
                      : (providerPhone && form.td_contact_phone === providerPhone ? "Teléfono del proveedor · cámbialo si te dan el del guía/conductor." : "Teléfono manual (guía/conductor).")}
                  </p>
                </div>
              </div>
            </>
          )}

          {/* Campos estructurados de RENTAL CAR — se sincronizan con KimKim
              (Confirmation #, Pick up / Drop off time & location). Reemplazan
              lo que antes se metía en las notas de la plantilla. */}
          {form.type === "rental_car" && (
            <div className="rounded-lg border-2 border-emerald-200 bg-emerald-50/40 p-3" data-testid="rd-rental-car-block">
              <div className="mb-2 flex items-center gap-2">
                <MapPin className="h-3.5 w-3.5 text-emerald-700" />
                <Label className="text-[10px] font-semibold uppercase tracking-wider text-emerald-900">
                  Datos operativos del rental car · se envían a KimKim como campos aparte
                </Label>
              </div>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-12">
                <div className="sm:col-span-4">
                  <FieldLabel label="Confirmation #" />
                  <Input className="h-9" value={form.confirmation_number || ""} onChange={(e) => set("confirmation_number", e.target.value)} placeholder="Ej. 9730205465" data-testid="rd-confirmation-number" />
                </div>
                <div className="sm:col-span-4">
                  <FieldLabel label="Fecha devolución" />
                  <Input type="date" className="h-9" value={(form.date_exit || "").slice(0, 10)} onChange={(e) => set("date_exit", e.target.value)} data-testid="rd-date-exit" />
                </div>
                <div className="sm:col-span-4">
                  <FieldLabel label="Hora devolución" />
                  <Input className="h-9" value={form.hour_end || ""} onChange={(e) => set("hour_end", e.target.value)} placeholder="Ej. 10:30" data-testid="rd-hour-end" />
                </div>
                <div className="sm:col-span-6">
                  <FieldLabel icon={MapPin} label="Lugar recogida" />
                  <Input className="h-9" value={form.start_location || ""} onChange={(e) => set("start_location", e.target.value)} placeholder="Ej. Madrid Atocha Estación" data-testid="rd-start-location" />
                </div>
                <div className="sm:col-span-6">
                  <FieldLabel icon={MapPin} label="Lugar devolución" />
                  <Input className="h-9" value={form.end_location || ""} onChange={(e) => set("end_location", e.target.value)} placeholder="Ej. Madrid Atocha Estación" data-testid="rd-end-location" />
                </div>
              </div>
              <p className="mt-2 text-[11px] text-emerald-800/80">
                Estos campos se envían al modal <em>Confirmation details</em> de KimKim en columnas separadas. Al pulsar «Cargar información del voucher» en la pestaña «Reserva», se rellenan automáticamente con los datos del PDF del proveedor.
              </p>
            </div>
          )}

          {/* Ficha del hotel: se ha movido a la pestaña «Template» (2026-02-28) */}

          <Separator />

          {/* ═══ COTIZACIÓN (integrada · antes de Estados y Facturación) ═══
              Layout partido en 2 mitades iguales: título grande a la izquierda,
              bloque izquierdo (Moneda + KK + Real) 50%, separador vertical central,
              bloque derecho (Sin IVA + Con IVA) 50%. Alojamiento con habitaciones
              estructuradas conserva el sub-panel Coste Real Total (desde habitaciones). */}
          <div className="space-y-4" data-testid="rd-cotizacion-panel">
            {roomsMode ? (
              <>
                <div className="flex items-center gap-2">
                  <Calculator className="h-5 w-5 text-emerald-600" />
                  <h2 className="font-heading text-xl font-bold">Cotización</h2>
                  <span className="ml-auto text-sm font-bold text-emerald-700 tabular-nums">{fmtEUR(accTotalExcl)}</span>
                </div>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-12">
                  <div className="sm:col-span-3">
                    <FieldLabel label="Moneda" />
                    <Select value={form.currency || "EUR"} onValueChange={(v) => set("currency", v)}>
                      <SelectTrigger className="h-9" data-testid="rd-currency"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="EUR">€ · EUR</SelectItem>
                        <SelectItem value="USD">$ · USD</SelectItem>
                        <SelectItem value="GBP">£ · GBP</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="sm:col-span-9 rounded-md border bg-muted/20 p-3" data-testid="rd-coste-proveedor">
                    <Label className="mb-2 flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                      <Bed className="h-3 w-3" /> Coste Real Total (desde habitaciones)
                    </Label>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <FieldLabel label="€/noche s/IVA" />
                        <Input readOnly disabled className="h-9 bg-muted/40 text-right tabular-nums" value={fmtEUR(sumNightExcl)} />
                      </div>
                      <div>
                        <FieldLabel label="€/noche c/IVA" />
                        <Input readOnly disabled className="h-9 bg-muted/40 text-right tabular-nums" value={fmtEUR(sumNightIncl)} />
                      </div>
                    </div>
                    <div className="mt-3 flex items-center justify-between border-t pt-2 text-xs">
                      <span className="text-muted-foreground">{nightsEff} noches × {rooms.length} hab</span>
                      <span className="tabular-nums">
                        <strong>{fmtEUR(accTotalExcl)}</strong> <span className="text-muted-foreground">s/IVA · </span>
                        <strong>{fmtEUR(accTotalIncl)}</strong> <span className="text-muted-foreground">c/IVA</span>
                      </span>
                    </div>
                  </div>
                </div>
              </>
            ) : (
              <>
                {/* Flex-wrap con Cotización título + 3 campos izq · separador · 2 campos der */}
                <div className="flex flex-wrap items-end gap-5" data-testid="rd-cot-inline">
                  <div className="flex items-center gap-2 pb-2">
                    <Calculator className="h-5 w-5 text-emerald-600" />
                    <h2 className="font-heading text-xl font-bold">Cotización</h2>
                  </div>
                  {/* Mitad izquierda · ámbito Cotización (Moneda / KimKim / Real) */}
                  <div className="flex flex-1 items-end justify-around gap-4">
                    <div className="w-24">
                      <FieldLabel label="Moneda" />
                      <Select value={form.currency || "EUR"} onValueChange={(v) => set("currency", v)}>
                        <SelectTrigger className="h-9" data-testid="rd-currency"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="EUR">€ · EUR</SelectItem>
                          <SelectItem value="USD">$ · USD</SelectItem>
                          <SelectItem value="GBP">£ · GBP</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="w-32">
                      <FieldLabel icon={Calculator} label="Cotizado KimKim" />
                      <Input type="number" step="0.01" className="h-9 text-right tabular-nums" value={form.kk_price_total ?? ""} onChange={(e) => set("kk_price_total", e.target.value)} data-testid="rd-kk-price" placeholder="0,00" />
                    </div>
                    <div className="w-32">
                      <FieldLabel label="Cotizado Real" className="text-emerald-700" />
                      <Input type="number" step="0.01" className="h-9 border-emerald-500/40 bg-emerald-500/[0.04] text-right font-semibold tabular-nums" value={form.cost_tax_excl ?? ""} onChange={(e) => set("cost_tax_excl", e.target.value)} data-testid="rd-cost-excl" placeholder="0,00" />
                    </div>
                  </div>
                  {/* Separador vertical central */}
                  <div className="mx-1 hidden h-12 w-px self-center bg-border sm:block" aria-hidden="true" />
                  {/* Mitad derecha · ámbito Factura Proveedor (COSTE único + Reembolsado)
                      El campo COSTE es único (unificamos "Sin IVA" + "Con IVA" del legacy).
                      Recordatorio para España: si la factura desglosa IVA, hay que
                      poner el importe SIN IVA (regla contable de Espíritu). */}
                  <div className="flex flex-1 items-start justify-around gap-4">
                    <div className="w-36" title="Factura real del proveedor. En España, si viene desglosada por IVA, poner SIN IVA.">
                      <FieldLabel icon={Receipt} label="Coste" />
                      <Input
                        type="number"
                        step="0.01"
                        className="h-9 text-right tabular-nums"
                        value={form.invoice_tax_excl ?? ""}
                        onChange={(e) => set("invoice_tax_excl", e.target.value)}
                        data-testid="rd-invoice-excl"
                        placeholder="—"
                      />
                      {(form.country || "").toLowerCase().includes("españa") && (
                        <p className="mt-1 text-[10px] leading-tight text-amber-700" data-testid="rd-coste-spain-hint">
                          Poner sin IVA si es factura desglosada
                        </p>
                      )}
                    </div>
                    <div className="w-32" title="Reembolsado al cliente por esta reserva (se suma al total del viaje)">
                      <FieldLabel icon={Receipt} label="Reembolsado (€)" />
                      <div className="relative">
                        <Input
                          type="number"
                          step="0.01"
                          className="h-9 pr-7 text-right tabular-nums"
                          value={form.reembolsado ?? ""}
                          onChange={(e) => set("reembolsado", e.target.value)}
                          data-testid="rd-reembolsado"
                          placeholder="0,00"
                        />
                        <span className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 text-sm font-semibold text-muted-foreground" data-testid="rd-reembolsado-eur">€</span>
                      </div>
                    </div>
                  </div>
                </div>
                <p className="text-[10px] text-muted-foreground">Precio siempre en conjunto (no por unidad) · Cotizado Real = KimKim − 8% (no aplica a alojamientos) · Coste = factura del proveedor (fase posterior).</p>
              </>
            )}
          </div>

          <Separator />

          {/* Notas · movido ARRIBA (03-ago-2026, Eduardo): contexto operativo
              antes que los estados auto-derivados que vienen debajo. */}
          <div>
            <FieldLabel icon={StickyNote} label="Notas" />
            <Textarea value={form.note || ""} onChange={(e) => set("note", e.target.value)} rows={3} data-testid="rd-notas" />
          </div>

          {/* Fila 4 · Estados y Facturación (oct-2026, Ventas): los 3 estados se pueden marcar
              a mano, pero al guardar se exige el soporte documental correspondiente:
                · Fact. Solicitada  ← manual (o auto si ya hay solicitud/factura)
                · Proforma/Voucher  ← manual · requiere ≥1 voucher subido
                · Estado Pago       ← manual · requiere ≥1 pago registrado (o auto si pagado ≥ coste)
                · Conciliado        ← manual */}
          <div>
            <Label className="mb-2 block text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Estados y Facturación</Label>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-5 sm:pr-40">
              <Select value={form.status_facturacion || "sin_factura"} onValueChange={(v) => set("status_facturacion", v)}>
                <SelectTrigger className="h-9" data-testid="rd-estado-facturacion">
                  <span className="mr-1 inline-flex items-center text-muted-foreground"><Receipt className="h-3.5 w-3.5" /></span>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>{FACT_ORDER.map((k) => <SelectItem key={k} value={k} data-testid={`rd-estado-facturacion-${k}`}>{RESERVA_FACTURACION_LABELS[k]}</SelectItem>)}</SelectContent>
              </Select>
              <ToggleH9 icon={FileSignature} label="Fact. Solicitada"
                        checked={!!(form.factura_solicitada || (form.factura_docs || []).length > 0)}
                        onChange={(v) => set("factura_solicitada", v)} testid="rd-fact-sol" />
              <ToggleH9 icon={ScrollText} label={`Proforma/Voucher${voucherCount ? ` (${voucherCount})` : ""}`}
                        checked={!!form.status_proforma_voucher || voucherCount > 0}
                        onChange={(v) => set("status_proforma_voucher", v)} testid="rd-proforma" />
              <ToggleH9 icon={ShieldCheck} label="Conciliado" checked={form.status_conciliado} onChange={(v) => set("status_conciliado", v)} testid="rd-conciliado" />
              <ToggleH9 icon={Coins} label={`Pagado${pagosValidos ? ` (${pagosValidos} pago${pagosValidos > 1 ? "s" : ""})` : ""}`} checked={!!form.status_pago} onChange={(v) => set("status_pago", v)} testid="rd-pago" />
            </div>
            <p className="mt-1.5 text-[10px] text-muted-foreground" data-testid="rd-estados-hint">Para guardar: «Factura» necesita un archivo subido · «Pagado» necesita un pago registrado · «Reservado» y «Proforma/Voucher» necesitan un voucher.</p>
          </div>
        </div>
      </section>

      {/* Pestañas de la reserva (estilo SOFI 1.0) */}
      <Tabs defaultValue="pagos">
        <TabsList className="flex-wrap" data-testid="reserva-tabs">
          <TabsTrigger value="pagos" data-testid="rt-pagos"><CreditCard className="mr-1 h-4 w-4" />Pagos</TabsTrigger>
          <TabsTrigger value="facturas" data-testid="rt-facturas"><Receipt className="mr-1 h-4 w-4" />Facturas</TabsTrigger>
          <TabsTrigger value="solicitud" data-testid="rt-solicitud"><FileSignature className="mr-1 h-4 w-4" />Solicitud de factura</TabsTrigger>
          <TabsTrigger value="vouchers" data-testid="rt-vouchers"><FileText className="mr-1 h-4 w-4" />Vouchers</TabsTrigger>
          <TabsTrigger value="recordatorios" data-testid="rt-recordatorios"><Bell className="mr-1 h-4 w-4" />Recordatorios</TabsTrigger>
          <TabsTrigger value="template" data-testid="rt-template"><LayoutTemplate className="mr-1 h-4 w-4" />Template</TabsTrigger>
          <TabsTrigger value="chat" data-testid="rt-chat"><MessageSquare className="mr-1 h-4 w-4" />Chat</TabsTrigger>
        </TabsList>

        {/* PAGOS — pagos y devoluciones al proveedor (en €) */}
        <TabsContent value="pagos" className="mt-4">
          {(() => {
            const qty = 1; // coste = TOTAL de la reserva, sin multiplicar (Dirección, oct-2026)
            const invIncl = num(form.invoice_tax_incl);
            const totalAPagar = invIncl > 0
              ? round2(invIncl)
              : round2(num(form.cost_tax_incl) * qty);
            const pendiente = round2(totalAPagar - pagadoNeto);
            const MedioSelect = ({ p, i }) => (
              <Select value={p.medio || undefined} onValueChange={(v) => setPago(i, "medio", v)}>
                <SelectTrigger data-testid={`rd-pago-medio-${i}`}><SelectValue placeholder="Elegir…" /></SelectTrigger>
                <SelectContent className="max-h-72">
                  {p.medio && !medioLabels.has(p.medio) && <SelectItem value={p.medio}>{p.medio}</SelectItem>}
                  {["tarjeta", "transferencia", "domiciliacion"].map((t) => (
                    (mediosByTipo[t] && mediosByTipo[t].length) ? (
                      <SelectGroup key={t}>
                        <SelectLabel>{TIPO_GRP[t]}</SelectLabel>
                        {mediosByTipo[t].map((m) => <SelectItem key={m.id} value={m.label}>{m.nombre}</SelectItem>)}
                      </SelectGroup>
                    ) : null
                  ))}
                </SelectContent>
              </Select>
            );
            return (
          <Card><CardContent className="space-y-4 p-5">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Pagos y devoluciones</Label>
              <div className="flex gap-2">
                <Button type="button" size="sm" variant="outline" onClick={addPago} data-testid="rd-add-pago"><Plus className="mr-1 h-4 w-4" />Añadir pago</Button>
                <Button type="button" size="sm" variant="outline" onClick={addDevolucion} data-testid="rd-add-devolucion"><RotateCcw className="mr-1 h-4 w-4 text-amber-600" />Añadir devolución</Button>
              </div>
            </div>
            {(form.pagos || []).length === 0 ? (
              <p className="py-3 text-center text-sm text-muted-foreground" data-testid="rd-pagos-empty">Sin movimientos. Pulsa «Añadir pago» o «Añadir devolución».</p>
            ) : (
              <div className="space-y-1.5" data-testid="rd-pagos-list">
                {/* Cabecera de columnas */}
                <div className="hidden grid-cols-[96px_150px_minmax(0,1fr)_120px_minmax(0,1.4fr)_40px] gap-2 px-2 pb-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground sm:grid">
                  <span>Tipo</span>
                  <span>Fecha de pago</span>
                  <span>Método de pago</span>
                  <span>Cantidad (€)</span>
                  <span>Notas</span>
                  <span />
                </div>
                {(form.pagos || []).map((p, i) => {
                  const isDev = p.tipo === "devolucion";
                  return (
                  <div key={i} className={`grid grid-cols-1 items-center gap-2 rounded-md border p-2 sm:grid-cols-[96px_150px_minmax(0,1fr)_120px_minmax(0,1.4fr)_40px] ${isDev ? "border-amber-300 bg-amber-50/60" : ""}`} data-testid={`rd-pago-row-${i}`}>
                    <Badge variant={isDev ? "outline" : "secondary"} className={`h-6 w-fit whitespace-nowrap ${isDev ? "border-amber-400 text-amber-700" : ""}`} data-testid={`rd-pago-tipo-${i}`}>{isDev ? "Devolución" : "Pago"}</Badge>
                    <Input type="date" value={(p.fecha || "").slice(0, 10)} onChange={(e) => setPago(i, "fecha", e.target.value)} aria-label="Fecha de pago" data-testid={`rd-pago-fecha-${i}`} />
                    <MedioSelect p={p} i={i} />
                    <Input type="number" step="0.01" min="0" placeholder="0,00" value={p.cantidad ?? ""} onChange={(e) => setPago(i, "cantidad", e.target.value)} aria-label="Cantidad" data-testid={`rd-pago-cantidad-${i}`} />
                    <Input value={p.notas ?? ""} onChange={(e) => setPago(i, "notas", e.target.value)} placeholder="Notas (opcional)" aria-label="Notas" data-testid={`rd-pago-notas-${i}`} />
                    <Button type="button" variant="ghost" size="icon" onClick={() => delPago(i)} data-testid={`rd-pago-del-${i}`}><Trash2 className="h-4 w-4 text-destructive" /></Button>
                  </div>
                  );
                })}
              </div>
            )}
            <Separator />
            {/* Resumen: total factura (IVA) vs pagado neto → pendiente */}
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4" data-testid="rd-pagos-resumen">
              <div className="rounded-md border p-3"><p className="text-[11px] uppercase text-muted-foreground">Total a pagar (factura IVA)</p><p className="mt-0.5 text-lg font-bold tabular-nums">{fmtEUR(totalAPagar)}</p></div>
              <div className="rounded-md border p-3"><p className="text-[11px] uppercase text-muted-foreground">Pagado</p><p className="mt-0.5 text-lg font-semibold tabular-nums text-emerald-700" data-testid="rd-pagos-pagado">{fmtEUR(pagosSum)}</p></div>
              <div className="rounded-md border p-3"><p className="text-[11px] uppercase text-muted-foreground">Devoluciones</p><p className="mt-0.5 text-lg font-semibold tabular-nums text-amber-700" data-testid="rd-pagos-devoluciones">{devolucionesSum ? `− ${fmtEUR(devolucionesSum)}` : fmtEUR(0)}</p></div>
              <div className={`rounded-md border p-3 ${pendiente > 0.01 ? "border-red-300 bg-red-50/60" : pendiente < -0.01 ? "border-amber-300 bg-amber-50/60" : totalAPagar > 0 ? "border-emerald-300 bg-emerald-50/60" : ""}`}><p className="text-[11px] uppercase text-muted-foreground">{pendiente < -0.01 ? "Sobrepagado" : (pendiente <= 0.01 && totalAPagar > 0) ? "Pagado" : "Pendiente"}</p><p className={`mt-0.5 text-lg font-bold tabular-nums ${pendiente > 0.01 ? "text-red-700" : pendiente < -0.01 ? "text-amber-700" : totalAPagar > 0 ? "text-emerald-700" : ""}`} data-testid="rd-pagos-pendiente">{(pendiente <= 0.01 && pendiente >= -0.01 && totalAPagar > 0) ? "Pagado" : fmtEUR(Math.abs(pendiente))}</p></div>
            </div>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <SW label="Estado Conciliado" checked={form.status_conciliado} onChange={(v) => set("status_conciliado", v)} testid="rd-conciliado-2" />
            </div>
          </CardContent></Card>
            );
          })()}
        </TabsContent>

        {/* FACTURAS — factura del proveedor (SOFI 1.0) */}
        <TabsContent value="facturas" className="mt-4">
          <div className="space-y-4">
          <Card><CardContent className="grid grid-cols-1 gap-3 p-5 sm:grid-cols-2">
            <div className="space-y-2 sm:col-span-2">
              <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Estado Facturación</Label>
              <Select value={form.status_facturacion || "sin_factura"} onValueChange={(v) => set("status_facturacion", v)}>
                <SelectTrigger data-testid="rd-fact-estado-2"><SelectValue /></SelectTrigger>
                <SelectContent>{FACT_ORDER.map((k) => <SelectItem key={k} value={k}>{RESERVA_FACTURACION_LABELS[k]}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <F label="F. sin IVA (coste real factura)"><Input type="number" step="0.01" value={form.invoice_tax_excl ?? ""} onChange={(e) => set("invoice_tax_excl", e.target.value)} data-testid="rd-inv-excl" /></F>
            <F label="F. con IVA (coste real factura)"><Input type="number" step="0.01" value={form.invoice_tax_incl ?? ""} onChange={(e) => set("invoice_tax_incl", e.target.value)} data-testid="rd-inv-incl" /></F>
            <SW label="Proveedor mensual (facturación mensual)" checked={form.proveedor_mensual} onChange={(v) => set("proveedor_mensual", v)} testid="rd-prov-mensual" />
          </CardContent></Card>
          <FacturaIA tripId={tripId} reservaId={reserva.id} reservaType={RESERVA_TYPE_LABELS[form.type] || form.type} />
          </div>
        </TabsContent>

        {/* SOLICITUD DE FACTURA */}
        <TabsContent value="solicitud" className="mt-4">
          <SolicitudFacturaReserva tripId={tripId} reservaId={reserva.id} onChanged={onChanged} />
        </TabsContent>

        {/* VOUCHERS — bonos emitidos por el PROVEEDOR del servicio (se guardan para enviar al cliente / Travel Document) */}
        <TabsContent value="vouchers" className="mt-4">
          <VouchersTab tripId={tripId} reservaId={reserva.id} />
        </TabsContent>

        {/* RECORDATORIOS */}
        <TabsContent value="recordatorios" className="mt-4">
          <ReservaRecordatorios tripId={tripId} reservaId={reserva.id} recs={recs} onChanged={onChanged} />
        </TabsContent>

        {/* TEMPLATE — plantilla TD (Travel Document) para KimKim
            + ficha de hotel (habitaciones estructuradas) cuando aplique. */}
        <TabsContent value="template" className="mt-4">
          {form.type === "alojamiento" && (
            <Card className="mb-4"><CardContent className="p-5" data-testid="rd-hotel-card">
              {/* Ficha del hotel — datos básicos */}
              <div>
                <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                  <div className="flex items-center gap-2">
                    <Building2 className="h-4 w-4 text-primary" />
                    <span className="font-heading text-base font-bold">Ficha del hotel</span>
                    <span className="text-xs text-muted-foreground">· aparece en Travel Document</span>
                  </div>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    className="h-7 gap-1 border-blue-400/60 bg-blue-50/60 text-xs text-blue-800 hover:bg-blue-100"
                    onClick={() => runGoogleLookup({ force: true })}
                    disabled={googleBusy}
                    data-testid="rd-hotel-google-lookup-btn"
                    title="Autocompletar dirección, web y teléfono usando Google Maps"
                  >
                    {googleBusy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Sparkles className="h-3 w-3" />}
                    Autocompletar desde Google
                  </Button>
                </div>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-12">
                  <div className="sm:col-span-5">
                    <FieldLabel label="Nombre del hotel" />
                    <HotelNameAutocomplete
                      value={form.hotel_name || ""}
                      onChange={(v) => set("hotel_name", v)}
                      onApply={(hit) => setForm((s) => ({
                        ...s,
                        hotel_name: hit.name || s.hotel_name || "",
                        hotel_address: hit.address || s.hotel_address || "",
                        hotel_website: hit.website || s.hotel_website || "",
                        hotel_phone: hit.phone || s.hotel_phone || "",
                      }))}
                      onBlur={(v) => {
                        // Auto-trigger silencioso solo si falta dirección o web,
                        // el nombre tiene ≥3 chars y no lo hemos consultado ya.
                        const missing = !form.hotel_address || !form.hotel_website;
                        if (missing && (v || "").trim().length >= 3) {
                          runGoogleLookup({ silent: true });
                        }
                      }}
                      placeholder="Ej. Carnival Palace"
                      testid="rd-hotel-name"
                    />
                  </div>
                  <div className="sm:col-span-7">
                    <FieldLabel label="Dirección" />
                    <Input className="h-9" value={form.hotel_address || ""} onChange={(e) => set("hotel_address", e.target.value)} placeholder="Cannaregio 929, Venezia, Italy" data-testid="rd-hotel-address" />
                  </div>
                  <div className="sm:col-span-6">
                    <FieldLabel label="Web del hotel" />
                    <Input className="h-9" type="url" value={form.hotel_website || ""} onChange={(e) => set("hotel_website", e.target.value)} placeholder="https://www.carnivalpalace.com" data-testid="rd-hotel-website" />
                  </div>
                  <div className="sm:col-span-3">
                    <FieldLabel label="Teléfono" />
                    <Input className="h-9" type="tel" value={form.hotel_phone || ""} onChange={(e) => set("hotel_phone", e.target.value)} placeholder="+34 951 30 71 90" data-testid="rd-hotel-phone" />
                  </div>
                  <div className="sm:col-span-3">
                    <FieldLabel label="Ocultar web" />
                    <ToggleH9 icon={Eye} label="Ocultar web" checked={form.hide_website} onChange={(v) => set("hide_website", v)} testid="rd-hide-website" />
                  </div>
                </div>
                <p className="mt-3 text-[11px] text-muted-foreground">
                  Al guardar, los datos se añaden al directorio de hoteles y se autocompletan la próxima vez que reserves este mismo hotel.
                </p>
              </div>
            </CardContent></Card>
          )}
          <Card><CardContent className="p-5" data-testid="rt-template-content">
            <ReservaTemplate
              reserva={form}
              trip={trip}
              value={{ plantilla_id: form.plantilla_id, plantilla_name: form.plantilla_name, sales_description: form.sales_description, sales_images: form.sales_images }}
              onChange={merge}
              tripId={tripId}
              reservaId={form.id}
              kimkimUrl={trip.kimkim_trip_url}
            />
          </CardContent></Card>
        </TabsContent>

        {/* CHAT */}
        <TabsContent value="chat" className="mt-4">
          <ReservaChat reserva={reserva} trip={trip} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

const fmtBytes = (n) => {
  if (!n && n !== 0) return "";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
};
const isImg = (ct) => (ct || "").startsWith("image/");

function VouchersTab({ tripId, reservaId }) {
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [shareOnUpload, setShareOnUpload] = useState(true);
  const [analyzing, setAnalyzing] = useState({});  // {voucher_id: true}
  const qc = useQueryClient();
  const { data, isLoading, refetch } = useQuery({
    queryKey: ["vouchers", tripId, reservaId],
    queryFn: async () => (await api.get(`/trips/${tripId}/reservas/${reservaId}/vouchers`)).data,
    // Auto-refetch mientras haya un voucher con análisis IA en curso (subida
    // reciente, background task todavía ejecutándose).
    refetchInterval: (query) => {
      const vs = query.state.data?.vouchers || [];
      const pending = vs.some((v) => v.ai_analysis === undefined && v.content_type?.startsWith("application/pdf"));
      return pending ? 4000 : false;
    },
  });
  const vouchers = data?.vouchers || [];

  const onFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;
    const fd = new FormData();
    files.forEach((f) => fd.append("files", f));
    fd.append("share_td", shareOnUpload ? "true" : "false");
    setBusy(true);
    try {
      const { data: resp } = await api.post(`/trips/${tripId}/reservas/${reservaId}/vouchers`, fd, {
        headers: { "Content-Type": "multipart/form-data" }, timeout: 120000,
      });
      toast.success(files.length > 1 ? `${files.length} vouchers guardados` : "Voucher guardado");
      if ((resp.ai_pending || []).length > 0) {
        toast.info("🤖 Analizando con IA en segundo plano…", { duration: 3500 });
      }
      refetch();
    } catch (err) {
      toast.error(apiErrorMessage(err, "No se pudo subir el voucher"));
    } finally {
      setBusy(false);
    }
  };

  const onReanalyze = async (v) => {
    setAnalyzing((s) => ({ ...s, [v.id]: true }));
    try {
      await api.post(`/trips/${tripId}/reservas/${reservaId}/vouchers/${v.id}/analyze`, {}, { timeout: 90000 });
      toast.success("Voucher re-analizado con IA");
      refetch();
    } catch (err) {
      toast.error(apiErrorMessage(err, "No se pudo analizar el voucher"));
    } finally {
      setAnalyzing((s) => ({ ...s, [v.id]: false }));
    }
  };

  const onToggleShare = async (v, next) => {
    try {
      await api.put(`/trips/${tripId}/reservas/${reservaId}/vouchers/${v.id}/share`, { shared: next });
      refetch();
    } catch (err) {
      toast.error(apiErrorMessage(err, "No se pudo cambiar"));
    }
  };

  const onView = async (v) => {
    // Voucher externo (link) → abrir en nueva pestaña
    if (v.external_url) {
      window.open(v.external_url, "_blank", "noopener,noreferrer");
      return;
    }
    // Voucher roto (0 B o URL con placeholder legacy) → no se puede abrir
    if (v.broken) {
      toast.error("Archivo no disponible · re-solicita al proveedor");
      return;
    }
    try {
      const res = await api.get(`/trips/${tripId}/reservas/${reservaId}/vouchers/${v.id}/file`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (err) {
      toast.error(apiErrorMessage(err));
    }
  };

  const onDelete = async (v) => {
    if (!window.confirm(`¿Eliminar el voucher "${v.filename}"?`)) return;
    setBusy(true);
    try {
      await api.delete(`/trips/${tripId}/reservas/${reservaId}/vouchers/${v.id}`);
      toast.success("Voucher eliminado");
      refetch();
    } catch (err) {
      toast.error(apiErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card data-testid="vouchers-tab"><CardContent className="space-y-4 p-5">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="flex items-center gap-2">
          <FileText className="h-5 w-5 text-primary" />
          <div>
            <h3 className="font-semibold">Vouchers del proveedor</h3>
            <p className="text-xs text-muted-foreground">Guarda aquí los bonos/vouchers que ha emitido el proveedor del servicio (PDF o imagen) para luego enviárselos al cliente o adjuntarlos al Travel Document.</p>
          </div>
          {vouchers.length > 0 && <Badge variant="secondary" data-testid="vouchers-count">{vouchers.length}</Badge>}
        </div>
        {vouchers.length > 0 && (
          <div className="flex items-center gap-3">
            <label className="flex cursor-pointer items-center gap-2 text-xs text-muted-foreground" data-testid="vouchers-share-toggle">
              <Switch checked={shareOnUpload} onCheckedChange={setShareOnUpload} data-testid="vouchers-share-switch" />
              Compartir en Travel Document
            </label>
            <Button size="sm" onClick={() => fileRef.current?.click()} disabled={busy} data-testid="vouchers-add">
              {busy ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Upload className="mr-1.5 h-4 w-4" />}Subir voucher
            </Button>
          </div>
        )}
      </div>

      <input ref={fileRef} type="file" multiple accept="application/pdf,image/*,.pdf,.jpg,.jpeg,.png,.webp,.heic" className="hidden" onChange={onFiles} data-testid="vouchers-file-input" />

      {isLoading ? (
        <Skeleton className="h-24 w-full" />
      ) : vouchers.length === 0 ? (
        <div className="space-y-3">
          <label className="flex w-fit cursor-pointer items-center gap-2 text-xs text-muted-foreground" data-testid="vouchers-share-toggle-empty">
            <Switch checked={shareOnUpload} onCheckedChange={setShareOnUpload} data-testid="vouchers-share-switch-empty" />
            Compartir en Travel Document al subir
          </label>
          <button type="button" onClick={() => fileRef.current?.click()} disabled={busy}
            className="flex w-full flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed border-muted-foreground/30 bg-muted/30 px-6 py-10 text-center transition-colors hover:border-primary/50 hover:bg-muted/50 disabled:opacity-60"
            data-testid="vouchers-dropzone">
            {busy ? <Loader2 className="h-7 w-7 animate-spin text-primary" /> : <Upload className="h-7 w-7 text-muted-foreground" />}
            <span className="text-sm font-medium">Subir vouchers del proveedor</span>
            <span className="text-xs text-muted-foreground">PDF o imagen · puedes seleccionar varios a la vez</span>
          </button>
        </div>
      ) : (
        <div className="space-y-2" data-testid="vouchers-list">
          {vouchers.map((v) => (
            <div key={v.id} data-testid={`voucher-row-${v.id}`}>
              <div className="flex items-center justify-between gap-3 rounded-lg border bg-card px-3 py-2.5">
              <div className="flex min-w-0 items-center gap-3">
                <div className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${
                  v.broken ? "bg-red-100 text-red-600" :
                  v.external_url ? "bg-sky-100 text-sky-600" :
                  "bg-muted text-muted-foreground"}`}>
                  {v.external_url ? <ExternalLink className="h-4 w-4" /> :
                   v.broken ? <AlertTriangle className="h-4 w-4" /> :
                   isImg(v.content_type) ? <ImageIcon className="h-4 w-4" /> : <FileText className="h-4 w-4" />}
                </div>
                <div className="min-w-0">
                  <p className="truncate text-sm font-medium" title={v.filename}>
                    {v.filename}
                    {v.source === "fareharbor_webhook" && (
                      <span className="ml-1.5 rounded bg-sky-100 px-1 py-0.5 text-[9px] font-semibold uppercase tracking-wide text-sky-700">FH</span>
                    )}
                    {v.broken && (
                      <span className="ml-1.5 rounded bg-red-100 px-1 py-0.5 text-[9px] font-semibold uppercase tracking-wide text-red-700" title="Archivo no disponible · re-solicítalo al proveedor">
                        No disponible
                      </span>
                    )}
                  </p>
                  <p className="text-[11px] text-muted-foreground">
                    {v.external_url ? "Link externo · abrir en nueva pestaña"
                      : v.broken ? "Archivo vacío o URL corrupta (migración SOFI 1.0)"
                      : `${fmtBytes(v.size)}${v.uploaded_by ? ` · ${v.uploaded_by}` : ""}`}
                  </p>
                </div>
              </div>
              <div className="flex shrink-0 items-center gap-2">
                <label className={`flex items-center gap-1.5 text-[11px] ${v.broken ? "cursor-not-allowed opacity-50" : "cursor-pointer text-muted-foreground"}`}
                       title={v.broken ? "No se puede compartir un archivo no disponible" : "Compartir este voucher en el Travel Document"}>
                  <Switch checked={!!v.share_td} disabled={!!v.broken} onCheckedChange={(next) => onToggleShare(v, next)} data-testid={`voucher-share-${v.id}`} />
                  En TD
                </label>
                <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => onView(v)}
                        title={v.external_url ? "Abrir en nueva pestaña" : v.broken ? "Archivo no disponible" : "Ver"}
                        data-testid={`voucher-view-${v.id}`}>
                  {v.external_url ? <ExternalLink className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </Button>
                <Button size="icon" variant="ghost" className="h-8 w-8 text-destructive" onClick={() => onDelete(v)} disabled={busy} title="Eliminar" data-testid={`voucher-del-${v.id}`}><Trash2 className="h-4 w-4" /></Button>
              </div>
              </div>
              {/* Panel de análisis IA (solo lectura). El botón «Cargar» está
                  ahora dentro de la pestaña «Plantilla TD» de la reserva, donde
                  encaja mejor con el flujo de edición del template. */}
              <VoucherAiAnalysis
                voucher={v}
                analyzing={!!analyzing[v.id]}
                onReanalyze={() => onReanalyze(v)}
              />
            </div>
          ))}
        </div>
      )}
    </CardContent></Card>
  );
}


// -----------------------------------------------------------------------------
// Voucher AI Analysis panel
// Muestra los datos extraídos por Gemini del voucher del proveedor y su
// comparación con los datos guardados en la reserva. Solo aparece para
// reservas soportadas (rental_car, etc.).
function VoucherAiAnalysis({ voucher, analyzing, onReanalyze }) {
  const [open, setOpen] = useState(true);
  const a = voucher.ai_analysis;

  // Sin ai_analysis y sin analyzing = no soportado o pendiente inicial
  if (analyzing || (a === null || a === undefined)) {
    if (voucher.content_type !== "application/pdf" && !analyzing) return null;
    return (
      <div className="ml-11 mt-1 flex items-center gap-2 rounded-md border border-dashed border-muted-foreground/30 bg-muted/20 px-3 py-2 text-[11px] text-muted-foreground">
        <Loader2 className="h-3.5 w-3.5 animate-spin" />
        <span>Analizando voucher con IA…</span>
      </div>
    );
  }
  if (a.status === "unsupported") return null;
  if (a.status === "error") {
    return (
      <div className="ml-11 mt-1 flex items-center gap-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-[11px] text-amber-800">
        <AlertTriangle className="h-3.5 w-3.5" />
        <span>No se pudo analizar con IA · {a.error}</span>
        <Button size="sm" variant="outline" onClick={onReanalyze} className="ml-auto h-6 text-[10px]"
                data-testid={`voucher-ai-retry-${voucher.id}`}>Reintentar</Button>
      </div>
    );
  }
  const comparison = a.comparison || [];
  const plan = a.apply_plan || { actions: [], conflicts: [] };
  const warnings = plan.conflicts?.length || 0;
  const okCount = comparison.filter((c) => c.severity === "ok").length;
  const infoCount = comparison.filter((c) => c.severity === "info").length;

  return (
    <div
      className="ml-11 mt-1 rounded-md border border-violet-200 bg-violet-50/40"
      data-testid={`voucher-ai-panel-${voucher.id}`}
    >
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="flex w-full items-center justify-between px-3 py-2 text-[11px]"
        data-testid={`voucher-ai-toggle-${voucher.id}`}
      >
        <div className="flex items-center gap-2">
          <Sparkles className="h-3.5 w-3.5 text-violet-600" />
          <span className="font-semibold text-violet-800">Análisis IA del voucher</span>
          {warnings > 0 && (
            <span className="rounded-full bg-red-100 px-2 py-0.5 text-[10px] font-semibold text-red-900 ring-1 ring-red-300">
              ⚠ {warnings} discrepancia{warnings > 1 ? "s" : ""}
            </span>
          )}
          {warnings === 0 && okCount > 0 && (
            <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-semibold text-emerald-800">
              ✓ {okCount} coincidencia{okCount > 1 ? "s" : ""}
            </span>
          )}
          {infoCount > 0 && (
            <span className="text-[10px] text-muted-foreground">
              + {infoCount} dato{infoCount > 1 ? "s" : ""} nuevo{infoCount > 1 ? "s" : ""}
            </span>
          )}
          {a.applied_at && (
            <span className="ml-1 rounded-full bg-slate-100 px-2 py-0.5 text-[10px] text-slate-700">
              cargado {new Date(a.applied_at).toLocaleDateString("es-ES", { day: "2-digit", month: "2-digit" })}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
          <span className="rounded-full bg-violet-100 px-2 py-0.5 text-[10px] font-medium text-violet-700">
            usa el botón en «Plantilla TD» para cargar a la reserva
          </span>
          <span
            onClick={onReanalyze}
            className="cursor-pointer rounded px-2 py-0.5 text-[10px] font-medium text-violet-700 hover:bg-violet-100"
            data-testid={`voucher-ai-reanalyze-${voucher.id}`}
            title="Re-analizar con IA"
          >
            re-analizar
          </span>
          {open ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
        </div>
      </button>

      {/* Banner de discrepancias — MUY visible */}
      {warnings > 0 && open && (
        <div
          className="mx-3 mb-2 mt-1 rounded-md border-2 border-red-400 bg-red-50 px-3 py-2 shadow-sm"
          data-testid={`voucher-ai-conflicts-banner-${voucher.id}`}
        >
          <div className="flex items-start gap-2">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-red-600" />
            <div className="flex-1">
              <p className="text-xs font-bold uppercase tracking-wide text-red-800">
                ⚠ {warnings} discrepancia{warnings > 1 ? "s" : ""} entre voucher y SOFI
              </p>
              <p className="mt-0.5 text-[11px] text-red-700">
                Revisa cada línea: o pides al proveedor un voucher corregido, o corriges los datos en SOFI. No se sobrescribe nada automáticamente.
              </p>
              <ul className="mt-1.5 space-y-1">
                {plan.conflicts.map((c) => (
                  <li
                    key={c.key}
                    className="rounded bg-red-100/70 px-2 py-1 text-[11px]"
                    data-testid={`voucher-ai-conflict-${voucher.id}-${c.key}`}
                  >
                    <span className="font-semibold text-red-900">{c.display}:</span>{" "}
                    <span className="font-mono">SOFI = «{c.sofi_value}»</span>{" "}
                    <span className="text-red-500">≠</span>{" "}
                    <span className="font-mono">Voucher = «{c.voucher_value}»</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}

      {open && (
        <div className="grid grid-cols-1 gap-1 border-t border-violet-200/70 px-3 py-2 md:grid-cols-2">
          {comparison.map((c) => (
            <div
              key={c.key}
              className={`flex items-start gap-2 rounded px-2 py-1 text-[11px] ${
                c.severity === "warning" ? "bg-amber-100/70" :
                c.severity === "ok" ? "" : ""
              }`}
              data-testid={`voucher-ai-field-${voucher.id}-${c.key}`}
            >
              <span className="mt-[2px] shrink-0">
                {c.severity === "ok" ? (
                  <CheckCircle2 className="h-3 w-3 text-emerald-600" />
                ) : c.severity === "warning" ? (
                  <AlertTriangle className="h-3 w-3 text-amber-600" />
                ) : (
                  <Circle className="h-3 w-3 text-muted-foreground" />
                )}
              </span>
              <div className="min-w-0 flex-1">
                <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                  {c.display}
                </p>
                <p className="break-words text-[11px] leading-tight">
                  <span className="font-medium">{c.voucher_value}</span>
                  {c.severity === "warning" && c.sofi_value && (
                    <span className="ml-1 text-amber-800">
                      (en SOFI: <span className="font-mono">{c.sofi_value}</span>)
                    </span>
                  )}
                </p>
              </div>
            </div>
          ))}
          {comparison.length === 0 && (
            <p className="col-span-full text-[11px] text-muted-foreground italic">
              La IA no consiguió extraer datos claros de este voucher.
            </p>
          )}
        </div>
      )}
    </div>
  );
}


function ReservaRecordatorios({ tripId, reservaId, recs, onChanged }) {
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const add = async () => {
    if (!note.trim()) return;
    setBusy(true);
    try { await api.post(`/trips/${tripId}/recordatorios`, { note, reserva_id: reservaId }); setNote(""); toast.success("Recordatorio añadido"); onChanged && onChanged(); }
    catch (e) { toast.error(apiErrorMessage(e)); }
    setBusy(false);
  };
  const resolve = async (id) => { try { await api.patch(`/trips/${tripId}/recordatorios/${id}/resolve`); onChanged && onChanged(); } catch (e) { toast.error(apiErrorMessage(e)); } };
  const del = async (id) => { try { await api.delete(`/trips/${tripId}/recordatorios/${id}`); toast.success("Eliminado"); onChanged && onChanged(); } catch (e) { toast.error(apiErrorMessage(e)); } };

  return (
    <Card><CardContent className="space-y-3 p-5">
      <div className="flex gap-2">
        <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Nuevo recordatorio de esta reserva…" data-testid="rd-rec-note" />
        <Button onClick={add} disabled={busy || !note.trim()} data-testid="rd-rec-add"><Plus className="mr-1 h-4 w-4" />Añadir</Button>
      </div>
      {recs.length === 0 ? (
        <p className="py-4 text-center text-sm text-muted-foreground" data-testid="rd-rec-empty">Sin recordatorios para esta reserva.</p>
      ) : recs.map((r) => (
        <div key={r.id} className={`flex items-center gap-2 rounded-md border p-2 ${r.solved ? "opacity-60" : ""}`} data-testid={`rd-rec-${r.id}`}>
          <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => resolve(r.id)}>{r.solved ? <CheckCircle2 className="h-4 w-4 text-emerald-600" /> : <Circle className="h-4 w-4 text-muted-foreground" />}</Button>
          <span className={`flex-1 text-sm ${r.solved ? "line-through" : ""}`}>{r.note}</span>
          {r.fecha_recordatorio && <span className="text-xs text-muted-foreground">{fmtDate(r.fecha_recordatorio)}</span>}
          <Button size="icon" variant="ghost" className="h-7 w-7 text-destructive" onClick={() => del(r.id)}><Trash2 className="h-4 w-4" /></Button>
        </div>
      ))}
    </CardContent></Card>
  );
}
